import { Fish, Drumstick, Egg, Milk, Carrot, ShoppingBag, Waves } from 'lucide-react';

// One place resolving an API category to its artwork. Previously duplicated
// between CategoryCard (the /categories grid) and the homepage teaser, which
// let the two drift; the homepage strip needs the same mapping again, so it
// lives here.
//
// The API returns a fixed icon-hint string per admin category; mapping it to an
// actual icon is a frontend concern. Unmapped hints fall back to ShoppingBag.
export const ICON_BY_HINT = {
  meat: Drumstick,
  seafood: Fish,
  fish: Waves,
  eggs: Egg,
  dairy: Milk,
  groceries: ShoppingBag,
  vegetables: Carrot,
  'storefront-outline': ShoppingBag,
};

// categoryKey is `toCategoryKey(adminLabel)` from the backend (see
// freshnhomely-backend/src/utils/categorySlugs.js) — matching on it, rather
// than the shared iconHint, keys each photo to exactly one admin category.
// `goat` is the current key for Mutton; `mutton-goat-lamb` is its pre-migration
// slug, still served by older data, and maps to the same photo.
export const IMAGE_BY_CATEGORY_KEY = {
  chicken: '/chicken1.jpg',
  eggs: '/eggs.jpg',
  goat: '/mutton.jpg',
  'mutton-goat-lamb': '/mutton.jpg',
  seafood: '/fish.jpg',
  groceries: '/grocery.jpg',
  'fresh-fruits-and-vegetables': '/fruits.jpg',
  // `others.jpg` shows electronics, not groceries, so this category falls back
  // to its icon rather than showing an unrelated photo.
};

// Extremely subtle pastel identity per category, applied to the fallback icon
// tile (no photo) and the card body beneath the image — never behind the photo
// itself, so the product photography stays the visual focus.
export const TINT_BY_CATEGORY_KEY = {
  chicken: 'from-amber-50/60 to-white',
  goat: 'from-rose-50/50 to-white',
  'mutton-goat-lamb': 'from-rose-50/50 to-white',
  seafood: 'from-sky-50/60 to-white',
  'fresh-fruits-and-vegetables': 'from-emerald-50/60 to-white',
  groceries: 'from-orange-50/40 to-white',
  eggs: 'from-amber-50/40 to-white',
  others: 'from-violet-50/40 to-white',
};

export const DEFAULT_TINT = 'from-emerald-50/40 to-white';

/** Artwork for a category: local photo when one exists, else its icon. */
export function resolveCategoryVisual(categoryKey, iconHint) {
  return {
    image: IMAGE_BY_CATEGORY_KEY[categoryKey] || null,
    Icon: ICON_BY_HINT[iconHint] || ShoppingBag,
    tint: TINT_BY_CATEGORY_KEY[categoryKey] || DEFAULT_TINT,
  };
}

// Homepage category-rail photos: 200px square crops of the site's own
// category photographs (Others: its pantry photograph), cut with one framing
// rule so every disc has the same resolution and visual weight; CSS draws the
// circle. Keyed like IMAGE_BY_CATEGORY_KEY; a category without one shows its
// icon on a pale-green disc of the same size.
export const RAIL_IMAGE_BY_CATEGORY_KEY = {
  chicken: '/home/rail-chicken.webp',
  eggs: '/home/rail-eggs.webp',
  goat: '/home/rail-goat.webp',
  'mutton-goat-lamb': '/home/rail-goat.webp',
  seafood: '/home/rail-seafood.webp',
  groceries: '/home/rail-groceries.webp',
  'fresh-fruits-and-vegetables': '/home/rail-fresh-fruits-and-vegetables.webp',
  others: '/home/rail-others.webp',
};

/** Artwork for a homepage rail tile: its photo when one exists, else its icon. */
export function resolveRailVisual(categoryKey, iconHint) {
  const image = RAIL_IMAGE_BY_CATEGORY_KEY[categoryKey] || null;
  return { image, Icon: ICON_BY_HINT[iconHint] || ShoppingBag };
}

